import { useEffect, useRef, useState } from 'react'
import { createModel } from 'hox'
import { TablePaginationConfig } from 'antd/es/table/interface'
import { DEFAULT_FIRST_PAGE_NUM, DEFAULT_PAGE_SIZE } from '@/utils/constants'
import { IDangerDetail, IDangerSearchFields, dangerService } from './dangerService'
import useGlobalModel, { transPgToPagination } from '@/model/globalModel'
import { ISearchPage } from '@/types/AppTypes'
import { ModoulTypeList } from '@/types/TcoDs'
import { fleetService } from '@/views/base/fleet/fleetService'
import { message } from 'antd'
import { convertRes2Blob } from '@/utils/exportFile'

// 数据来源Tab类型：scheduling=排班提报，inspection=巡查员提报
export type DangerTabType = 'scheduling' | 'inspection'

export const useDangerModel = createModel(function () {
    const {
        user: { shopCode, createUser, userType, dealerName },
    } = useGlobalModel(({ user }) => [user])
    const settingTypeList = ModoulTypeList
    /**
     * 列表
     */
    //当前Tab：排班提报 / 巡查员提报
    const [dangerTab, setDangerTab] = useState<DangerTabType>('scheduling')
    //列表数据源
    const [dataSource, setDataSource] = useState<any[]>([])
    //查询条件
    const [searchParam, setSearchParam] = useState<IDangerSearchFields>({})
    //新增详情form
    const [detailAddForm, setDetailAddForm] = useState<IDangerDetail>({})
    //编辑详情form
    const [detailEditForm, setDetailEditForm] = useState<IDangerDetail>({})
    //分页参数
    const initPgCfg: TablePaginationConfig = { pageSize: DEFAULT_PAGE_SIZE, current: DEFAULT_FIRST_PAGE_NUM }
    const [pgCfg, setPgCfg] = useState<TablePaginationConfig>(initPgCfg)
    //表格多选id值
    const [selectedRowKeys, setSelectedRowKeys] = useState<IDangerDetail[]>([])
    const [SelectedId, setSelectedId] = useState('')
    //对账明细打开页面
    const [detailVisible, setDetailVisible] = useState(false)
    const [isModalVisible, setIsModalVisible] = useState(false)
    const [acceptModalVisible, setAcceptModalVisible] = useState(false)
    //验收弹窗对应的隐患记录（含车号、创建日期等）
    const [acceptRecord, setAcceptRecord] = useState<any>({})
    const [editModalVisible, setEditModalVisible] = useState(false)
    const [editFormData, setEditFormData] = useState<IDangerDetail>({})

    //新增，编辑状态判断
    const [mode, setMode] = useState<'add' | 'edit'>('add')
    const pgCfgRef = useRef<TablePaginationConfig>(initPgCfg)
    //新增详情form
    const [totalNum, setTotalNum] = useState<any>({})
    const [maintenanceId, setMaintenanceId] = useState<any>('')
    // 公司数据
    const [companyData, setCompanyData] = useState<any[]>([])
    // 维修类型列表
    const [maintenanceTypeList] = useState<any[]>([
        {
            label: '自修',
            value: 'self_repair',
        },
        {
            label: '外修',
            value: 'external_repair',
        },
    ])

    const [loading, setLoading] = useState<boolean>(false)
    useEffect(() => {
        pgCfgRef.current = pgCfg
    }, [pgCfg])
    //列表查询
    const loadData = async (page: ISearchPage = pgCfgRef.current, searchValue: IDangerSearchFields = searchParam, tab: DangerTabType = dangerTab) => {
        setLoading(true)
        const searchParams = {
            ...searchValue,
            createUserId: tab === 'scheduling' ? shopCode : undefined,
            status: tab === 'scheduling' ? 'NO' : undefined,
            pageSize: page?.pageSize ?? pgCfg.pageSize ?? DEFAULT_PAGE_SIZE,
            pageNum: (page?.current || page?.pageNum) ?? DEFAULT_FIRST_PAGE_NUM,
            createUser: tab === 'scheduling' ? (userType === '超级管理员' ? searchValue.createUser : createUser) : undefined,
            dealerName: tab === 'scheduling' ? dealerName || undefined : undefined,
        }

        if (searchParams?.createUser && typeof searchParams.createUser === 'object' && searchParams.createUser.length > 0) {
            searchParams.createUser = searchParams.createUser.join(',') || ''
        }
        let retData: any
        if (tab === 'inspection') {
            const res = await dangerService.inspectionPage(searchParams)
            retData = res.retData
        } else {
            const res = await dangerService.page(searchParams)
            retData = res.retData
        }
        const { records, ...pg } = retData
        setPgCfg(transPgToPagination(pg))
        setDataSource(records)
        setLoading(false)
    }
    //设置搜索value
    const updateSearchParam = (updateParam: IDangerSearchFields) => {
        setSearchParam({ ...searchParam, ...updateParam })
    }

    //重置搜索参数
    const resetSearchParam = () => {
        setSearchParam({})
        setSelectedRowKeys([])
        loadData(initPgCfg, {}).then(() => {})
    }

    //切换Tab
    const switchDangerTab = (tab: DangerTabType) => {
        if (tab === dangerTab) return
        setDangerTab(tab)
        setSearchParam({})
        setSelectedRowKeys([])
        setPgCfg(initPgCfg)
        //直接传入新tab，避免闭包读取到旧state
        loadData(initPgCfg, {}, tab)
    }

    // 获取公司列表
    const fetchCompanyList = async () => {
        try {
            let data = {
                type: 'company',
                createUserId: shopCode,
            }
            let { retData } = await fleetService.page(data)
            setCompanyData(
                retData?.map((item: any) => ({
                    label: item.institutionName,
                    value: item.institutionName,
                })) || [],
            )
        } catch (error) {
            setCompanyData([])
        }
    }

    // 更新隐患金额
    const updateAmount = async (data: { id: string; amount: number; inspectionCost: string }) => {
        try {
            const result = await dangerService.updateAmount(data)
            if (result.success) {
                return { success: true, message: '更新成功!' }
            } else {
                return { success: false, message: '更新失败: ' + result.retMsg }
            }
        } catch (error) {
            return { success: false, message: '更新失败: 网络错误' }
        }
    }

    //导出
    const exportListData = async () => {
        let ids: any = ''
        if (selectedRowKeys && selectedRowKeys.length > 0) {
            ids = selectedRowKeys.map((item) => item.id as string).toString()
        }
        try {
            const searchParams = {
                ...searchParam,
                createUserId: shopCode,
                createUser: userType === '超级管理员' ? searchParam.createUser : createUser,
                fleet: userType === '管理员' ? createUser : undefined,
                status: 'NO',
                pageSize: -1,
                pageNum: 1,
                ids: ids ? ids : '',
                dealerName: dealerName || undefined,
            }
            let result = await dangerService.exportList(searchParams)
            convertRes2Blob(result)
        } catch {
            message.error('导出失败')
        }
    }

    return {
        /***field***/
        dangerTab,
        searchParam,
        dataSource,
        selectedRowKeys,
        pgCfg,
        detailVisible,
        detailEditForm,
        detailAddForm,
        mode,
        SelectedId,
        settingTypeList,
        totalNum,
        loading,
        isModalVisible,
        maintenanceId,
        acceptModalVisible,
        acceptRecord,
        editModalVisible,
        editFormData,
        companyData,
        maintenanceTypeList,
        /***method***/
        switchDangerTab,
        setAcceptModalVisible,
        setAcceptRecord,
        setMaintenanceId,
        setIsModalVisible,
        setLoading,
        setTotalNum,
        setDataSource,
        setSelectedRowKeys,
        resetSearchParam,
        updateSearchParam,
        loadData,
        setDetailVisible,
        setDetailEditForm,
        setDetailAddForm,
        setMode,
        setSelectedId,
        setEditModalVisible,
        setEditFormData,
        updateAmount,
        fetchCompanyList,
        exportListData,
    }
})
